import { Tooltip } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

let tooltipCount = 0;

/**
 * TooltipCustom - Accessible tooltip following WAI-ARIA tooltip pattern.
 *
 * @param {string}  aria       - 'describedby' (extra info) | 'labelledby' (primary label, e.g. icon-only buttons)
 * @param {bool}    arrow      - Show arrow pointer
 * @param {node}    children   - Trigger element (must support ref forwarding)
 * @param {string}  placement  - Tooltip position (top | bottom | left | right)
 * @param {node}    title      - Tooltip content
 */
function TooltipCustom({ aria, arrow, children, classes, disabled, leaveDelay, placement, title }) {
  const [isOpen, setIsOpen] = useState(false);

  const idRef = useRef(null);
  if (idRef.current === null) {
    tooltipCount += 1;
    idRef.current = `tooltip-${tooltipCount}`;
  }
  const id = idRef.current;

  const ariaAttr = (disabled || !title)
    ? {}
    : aria === 'labelledby'
    ? { 'aria-labelledby': id }
    : { 'aria-describedby': id };
  const tooltipTitle = disabled || !title ? '' : <span id={id} role="tooltip">{title}</span>;

  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(e) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  function handleOpen() {
    setIsOpen(true);
  }

  function handleClose() {
    setIsOpen(false);
  }

  return (
    <Tooltip
      arrow={arrow}
      classes={classes}
      interactive
      leaveDelay={leaveDelay}
      onClose={handleClose}
      onOpen={handleOpen}
      open={isOpen}
      placement={placement}
      PopperProps={{ keepMounted: aria === 'labelledby' }}
      title={tooltipTitle}
    >
      {React.cloneElement(children, ariaAttr)}
    </Tooltip>
  );
}

TooltipCustom.propTypes = {
  aria: PropTypes.oneOf(['describedby', 'labelledby']),
  arrow: PropTypes.bool,
  children: PropTypes.element.isRequired,
  classes: PropTypes.object,
  disabled: PropTypes.bool,
  leaveDelay: PropTypes.number,
  placement: PropTypes.string,
  title: PropTypes.node.isRequired,
};

TooltipCustom.defaultProps = {
  aria: 'describedby',
  arrow: false,
  classes: {},
  disabled: false,
  leaveDelay: 0,
  placement: 'top',
};

export default TooltipCustom;
